import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { MENU_ITEMS } from "../menuData";
import bHero from "../assets/blog/blog_hero_banner.png";

// Inline Icons
const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    width="18"
    height="18"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    width="20"
    height="20"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CustomizeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    width="14"
    height="14"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MenuPage = () => {
  const location = useLocation();

  // Search & Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [dietFilter, setDietFilter] = useState("all"); // all, veg, non-veg
  const [activeCategory, setActiveCategory] = useState("");

  // Customization Modal State
  const [selectedItem, setSelectedItem] = useState(null);
  const [addonsSelections, setAddonsSelections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  // Group items by category
  const categories = [...new Set(MENU_ITEMS.map((item) => item.category))];

  const categoryRefs = useRef({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });

    // Set initial active category
    if (categories.length > 0) {
      setActiveCategory(categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle hash scroll on mount/change
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const targetId = decodeURIComponent(hash.substring(1));
      setActiveCategory(targetId);
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          const headerOffset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.scrollY - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 300);
    }
  }, [location]);

  // Filter items based on search and diet
  const getFilteredItems = (category) => {
    return MENU_ITEMS.filter((item) => {
      if (item.category !== category) return false;
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDiet = dietFilter === "all" || item.type === dietFilter;
      return matchesSearch && matchesDiet;
    });
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    const element = document.getElementById(cat);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  // Open custom modal for item addons
  const openCustomizeModal = (item) => {
    setSelectedItem(item);

    // Initialize addons selection structure
    const initialSelections = {};
    item.addons.forEach((group) => {
      initialSelections[group.name] = {
        min: group.min,
        max: group.max,
        selectionType: group.selectionType,
        selected: [],
      };
    });

    setAddonsSelections(initialSelections);
    setModalOpen(true);
  };

  // Handle checking/unchecking addon item
  const handleAddonToggle = (groupName, addonItem) => {
    const groupSelection = addonsSelections[groupName];
    const isSelected = groupSelection.selected.some(
      (i) => i.name === addonItem.name,
    );

    let updatedSelected = [...groupSelection.selected];

    if (groupSelection.selectionType === "S") {
      // Single selection type (Radio)
      updatedSelected = [addonItem];
    } else {
      // Multiple selection type (Checkbox)
      if (isSelected) {
        updatedSelected = updatedSelected.filter(
          (i) => i.name !== addonItem.name,
        );
      } else {
        if (updatedSelected.length < groupSelection.max) {
          updatedSelected.push(addonItem);
        }
      }
    }

    setAddonsSelections({
      ...addonsSelections,
      [groupName]: {
        ...groupSelection,
        selected: updatedSelected,
      },
    });
  };

  // Calculate customized item total price
  const calculateTotal = () => {
    if (!selectedItem) return 0;
    let total = selectedItem.price;
    Object.keys(addonsSelections).forEach((groupName) => {
      addonsSelections[groupName].selected.forEach((addon) => {
        total += addon.price;
      });
    });
    return total;
  };

  return (
    <div className="rest-page" style={{ backgroundColor: "#faf8f5" }}>
      <Header />

      {/* Menu Banner */}
      <section
        className="rest-blog-banner"
        style={{ backgroundImage: `url(${bHero})` }}
      >
        <div className="max-w-7xl mx-auto">
          <h1 className="font-sans">Our Menu & Customization</h1>
          <div className="rest-blog-breadcrumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Food Catalog</span>
          </div>
        </div>
      </section>

      {/* Interactive Catalog Layout */}
      <main className="rest-blog-container">
        {/* CSS keyframe overrides for mobile responsiveness */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes modalPopIn {
            from { opacity: 0; transform: scale(0.96); }
            to { opacity: 1; transform: scale(1); }
          }
          @media (max-width: 639px) {
            .animate-modal-mobile {
              animation: slideUp 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          }
          @media (min-width: 640px) {
            .animate-modal-desktop {
              animation: modalPopIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          }
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `,
          }}
        />

        {/* Top Control Bar (Search + Diet filters) */}
        <div className="bg-white p-5 md:p-6 rounded-xl border border-neutral-100 shadow-xs mb-8 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 relative z-30">
          {/* Search Box */}
          <div className="relative w-full md:w-96">
            <input
              type="text"
              className="rest-search-input w-full"
              placeholder="Search dishes (e.g. burger, flakesfriedchicken)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingRight: "44px" }}
            />
            <span className="absolute right-4 top-[38%] text-neutral-400">
              <SearchIcon />
            </span>
          </div>

          {/* Diet filters (Veg / Non-veg selectors) - Responsive sizes */}
          <div className="flex bg-[#faf8f5] p-1 rounded-full border border-neutral-200/50 w-full md:w-auto justify-between md:justify-start">
            <button
              onClick={() => setDietFilter("all")}
              className={`flex-1 md:flex-none px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-extrabold text-[10px] sm:text-xs uppercase tracking-wider transition-all duration-300 font-sans ${
                dietFilter === "all"
                  ? "bg-[#cd3a27] text-white"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              All Items
            </button>
            <button
              onClick={() => setDietFilter("veg")}
              className={`flex-1 md:flex-none px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full font-extrabold text-[10px] sm:text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-1.5 font-sans ${
                dietFilter === "veg"
                  ? "bg-emerald-600 text-white"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 border border-emerald-300 inline-block shrink-0" />
              Veg Only
            </button>
            <button
              onClick={() => setDietFilter("non-veg")}
              className={`flex-1 md:flex-none px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full font-extrabold text-[10px] sm:text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-1.5 font-sans ${
                dietFilter === "non-veg"
                  ? "bg-rose-700 text-white"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-600 border border-rose-400 inline-block shrink-0" />
              Non-Veg
            </button>
          </div>
        </div>

        {/* Category Swipe Bar for Mobile/Tablet (Visible below lg, sticky navigation) */}
        <div className="block lg:hidden sticky top-20 z-20 bg-[#faf8f5]/95 backdrop-blur-md pb-4 pt-1 mb-8 -mx-4 px-4 border-b border-neutral-200/40 overflow-x-auto scrollbar-none scroll-smooth">
          <div className="flex items-center gap-2 min-w-max">
            {categories.map((cat) => {
              const isActive = activeCategory === cat;
              const hasMatchingItems = getFilteredItems(cat).length > 0;
              if (!hasMatchingItems) return null;

              return (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                    isActive
                      ? "text-white bg-[#cd3a27] shadow-xs"
                      : "text-neutral-700 bg-white hover:bg-neutral-50 border border-neutral-200/50"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white" : "bg-neutral-400"}`}
                  />
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Responsive Catalog Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_2.2fr] gap-8 lg:gap-10">
          {/* Category Sidebar Navigation (Sticky left) */}
          <aside className="hidden lg:block">
            <div className="bg-white border border-neutral-100 rounded-xl p-5 shadow-xs sticky top-32 max-h-[75vh] overflow-y-auto scrollbar-thin">
              <h3 className="font-sans font-black text-sm uppercase tracking-widest text-[#071620] mb-5 border-b border-neutral-100 pb-3 text-left">
                Categories
              </h3>
              <ul className="rest-category-list">
                {categories.map((cat) => (
                  <li
                    key={cat}
                    onClick={() => handleCategoryClick(cat)}
                    className={`rest-category-item font-sans text-xs uppercase tracking-wider py-3.5 border-b border-dashed border-neutral-100 cursor-pointer ${
                      activeCategory === cat
                        ? "active font-black text-[#cd3a27]"
                        : ""
                    }`}
                    style={{ justifyContent: "flex-start", gap: "8px" }}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${activeCategory === cat ? "bg-[#cd3a27]" : "bg-neutral-300"}`}
                    />
                    {cat}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Scrolling items catalog on the right */}
          <div className="flex flex-col gap-8 md:gap-12">
            {categories.map((cat) => {
              const catItems = getFilteredItems(cat);
              if (catItems.length === 0) return null;

              return (
                <div
                  key={cat}
                  id={cat}
                  ref={(el) => (categoryRefs.current[cat] = el)}
                  className="bg-white border border-neutral-100 p-5 md:p-8 rounded-2xl shadow-xs scroll-mt-36"
                >
                  {/* Category Title */}
                  <h3 className="font-sans font-black text-lg text-[#0f172a] uppercase tracking-wide border-l-4 border-[#cd3a27] pl-4 mb-6 md:mb-8 text-left">
                    {cat}
                  </h3>

                  {/* Items list */}
                  <div className="flex flex-col gap-6 md:gap-8">
                    {catItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4 pb-5 md:pb-6 border-b border-neutral-100 last:border-b-0 last:pb-0 group"
                      >
                        {/* Details */}
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            {/* Diet Tag */}
                            <span
                              className={`w-3 h-3 flex items-center justify-center border rounded p-[1px] flex-shrink-0 ${
                                item.type === "veg"
                                  ? "border-emerald-500 text-emerald-500"
                                  : "border-rose-600 text-rose-600"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${item.type === "veg" ? "bg-emerald-500" : "bg-rose-600"}`}
                              />
                            </span>

                            <h4 className="font-sans font-extrabold text-sm sm:text-base text-[#0f172a] group-hover:text-[#cd3a27] transition-colors duration-200">
                              {item.name}
                            </h4>
                          </div>

                          <p className="text-[11px] sm:text-xs text-neutral-500 mt-2 max-w-xl leading-relaxed">
                            {item.description ||
                              "Freshly cooked to order using quality inputs."}
                          </p>

                          {/* Addon Badge */}
                          {item.addons && item.addons.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black text-orange-600 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded mt-3 font-mono uppercase tracking-wider">
                              ✨ Customizable
                            </span>
                          )}
                        </div>

                        {/* Price & Action button - Balanced responsive layout */}
                        <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-4 sm:gap-6 mt-3 sm:mt-0 shrink-0 border-t border-neutral-50 sm:border-0 pt-3 sm:pt-0">
                          <span className="font-sans font-black text-sm sm:text-base text-[#cd3a27]">
                            ₹{item.price}
                          </span>

                          {item.addons && item.addons.length > 0 ? (
                            <button
                              onClick={() => openCustomizeModal(item)}
                              className="bg-[#cd3a27] hover:bg-[#b02f1e] text-white font-sans text-[10px] font-black uppercase tracking-wider px-4 py-2.5 rounded flex items-center gap-1.5 transition-all duration-200 active:scale-95 shadow-sm"
                            >
                              <CustomizeIcon />
                              Customize
                            </button>
                          ) : (
                            <span className="text-[9px] font-bold text-neutral-400 border border-neutral-200 px-3 py-1.5 rounded bg-neutral-50">
                              Standard Item
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Interactive Customize Modal (Centers on Desktop, Slides from Bottom on Mobile) */}
      {modalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-xs transition-opacity duration-300">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full max-h-[88vh] sm:max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-modal-mobile sm:animate-modal-desktop relative">
            {/* Modal Header */}
            <div className="p-6 border-b border-neutral-100 flex justify-between items-start">
              <div>
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${selectedItem.type === "veg" ? "bg-emerald-500 border border-emerald-300" : "bg-rose-600 border border-rose-400"}`}
                />
                <h3 className="font-sans font-black text-base text-[#0f172a] inline-block align-middle">
                  Customize "{selectedItem.name}"
                </h3>
                <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed text-left">
                  {selectedItem.description ||
                    "Select your preferred choice of addons below."}
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-neutral-400 hover:text-neutral-950 border border-neutral-200 p-1.5 rounded-full hover:bg-neutral-50 transition-colors focus:outline-none"
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6 scrollbar-none">
              {selectedItem.addons.map((group) => {
                const groupSelection = addonsSelections[group.name] || {
                  selected: [],
                };

                return (
                  <div
                    key={group.name}
                    className="border-b border-neutral-100 last:border-b-0 pb-6 last:pb-0 text-left"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <h4 className="font-sans font-black text-xs text-[#0f172a] uppercase tracking-wider">
                          {group.name}
                        </h4>
                        <span className="text-[9px] text-neutral-400 font-mono font-bold uppercase">
                          {group.min === 0
                            ? "Optional"
                            : `Required (min ${group.min})`}{" "}
                          · max {group.max}
                        </span>
                      </div>
                      <span className="text-[9px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded font-mono uppercase">
                        {group.selectionType === "S"
                          ? "Select One"
                          : "Select Multiple"}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2">
                      {group.items.map((addon) => {
                        const isChecked = groupSelection.selected.some(
                          (i) => i.name === addon.name,
                        );

                        return (
                          <div
                            key={addon.name}
                            onClick={() => handleAddonToggle(group.name, addon)}
                            className={`flex justify-between items-center p-3 border rounded-xl cursor-pointer select-none transition-all duration-200 ${
                              isChecked
                                ? "border-[#cd3a27] bg-[#faf8f5]"
                                : "border-neutral-100 hover:border-neutral-200"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-4 h-4 flex items-center justify-center border transition-all ${
                                  group.selectionType === "S"
                                    ? "rounded-full"
                                    : "rounded"
                                } ${
                                  isChecked
                                    ? "border-[#cd3a27] bg-[#cd3a27] text-white"
                                    : "border-neutral-300 bg-white"
                                }`}
                              >
                                {isChecked && (
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    className="w-2.5 h-2.5"
                                  >
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </div>

                              <span className="text-xs font-bold text-[#0f172a] font-sans">
                                {addon.name}
                              </span>
                            </div>

                            <span className="text-xs font-black text-[#cd3a27] font-sans">
                              {addon.price > 0 ? `+ ₹${addon.price}` : "FREE"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-neutral-100 bg-[#faf8f5] flex justify-between items-center pb-8 sm:pb-6">
              <div>
                <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider block font-mono text-left">
                  Total price
                </span>
                <span className="font-sans font-black text-xl text-[#cd3a27]">
                  ₹{calculateTotal()}
                </span>
              </div>
              <button
                onClick={() => {
                  alert("Customization selection saved!");
                  setModalOpen(false);
                }}
                className="bg-[#cd3a27] hover:bg-[#b02f1e] text-white font-sans text-xs font-black uppercase tracking-widest px-6 py-3 rounded-lg shadow-md active:scale-95 transition-all duration-200"
              >
                Add Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MenuPage;
